import { CustomOrthoCamera } from "@/components/r3f/cameras/custom-ortho-camera";
import { ProjectOverviewBase } from "@/components/ui/project-overview";
import { useAnnotationPermissions } from "@/hooks/use-annotation-permissions";
import { Mode, ModeSceneProps } from "@/modes/mode";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectNextModeActiveTool } from "@/store/ui/ui-selectors";
import {
  activateTool,
  setNextModeActiveTool,
  ToolName,
} from "@/store/ui/ui-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  PERSISTENT_TOOLS,
  useDeactivateToolOnUnmount,
  useToggleToolVisibility,
} from "@/tools/use-toggle-tool-visibility";
import { selectHaveElementsAccurateWorldScale } from "@faro-lotv/project-source";
import { useEffect } from "react";
import { useCurrentScene } from "../mode-data-context";
import { SheetOverlay } from "./sheet-overlay";
import { SheetScene } from "./sheet-scene";
import { SHEET_MODE_INITIAL_STATE, SheetModeInitialState } from "./sheet-state";
import { SheetTransition } from "./sheet-transition";

export const sheetMode: Mode<SheetModeInitialState> = {
  name: "sheet",
  initialState: SHEET_MODE_INITIAL_STATE,
  customCamera: CustomOrthoCamera,
  hasCustomCanvasStyle: true,
  ModeScene({ initialState }: ModeSceneProps<SheetModeInitialState>) {
    const { activeSheets } = useCurrentScene();
    const haveSheetsAccurateWorldScale = useAppSelector(
      selectHaveElementsAccurateWorldScale(activeSheets),
    );

    const hasWritePermission = useAppSelector(selectHasWritePermission);
    const { canWriteAnnotations } = useAnnotationPermissions();
    const nextModeActiveTool = useAppSelector(selectNextModeActiveTool);
    const dispatch = useAppDispatch();

    useToggleToolVisibility(ToolName.desaturateSheet, true, true, false);
    useToggleToolVisibility(
      ToolName.measurement,
      haveSheetsAccurateWorldScale,
      true,
      false,
    );
    useToggleToolVisibility(
      ToolName.annotation,
      haveSheetsAccurateWorldScale && canWriteAnnotations,
      true,
      false,
    );
    useToggleToolVisibility(
      ToolName.editWaypoints,
      hasWritePermission,
      true,
      false,
    );
    useToggleToolVisibility(ToolName.screenshot, true, true, false);

    useDeactivateToolOnUnmount(PERSISTENT_TOOLS);

    useEffect(() => {
      if (nextModeActiveTool) {
        dispatch(activateTool(nextModeActiveTool));
        dispatch(setNextModeActiveTool(null));
      }
    }, [nextModeActiveTool, dispatch]);

    return <SheetScene initialState={initialState} />;
  },
  ModeTransition: SheetTransition,
  ModeOverlay: SheetOverlay,
  ModePanel: ProjectOverviewBase,
};
